import { getAppSettings } from '../firebase/services/appSettingsService';
import { db } from './firebase/config';
import { doc, collection, updateDoc, getDoc, query, where, getDocs, onSnapshot, addDoc, serverTimestamp } from 'firebase/firestore';
import { getAuth, signInWithCustomToken } from 'firebase/auth';
import axios from 'axios';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { storage } from './firebase/config';
import { getCurrentUserId, isAuthenticated } from '../utils/authUtils';

// URLs des fonctions Firebase déployées
const FIREBASE_FUNCTIONS_BASE_URL = 'https://us-central1-diotranscript.cloudfunctions.net';
const START_TRANSCRIPTION_URL = `${FIREBASE_FUNCTIONS_BASE_URL}/startTranscription`;
const SEND_EMAIL_URL = `${FIREBASE_FUNCTIONS_BASE_URL}/sendTranscriptionCompletionEmail`;
const GENERATE_SUMMARY_URL = `${FIREBASE_FUNCTIONS_BASE_URL}/generateSummary`;
const COST_STATISTICS_URL = `${FIREBASE_FUNCTIONS_BASE_URL}/getCostStatistics`;

/**
 * Service de transcription qui utilise les fonctions Firebase
 */
const transcriptionService = {
  /**
   * Démarre une transcription en utilisant la fonction Firebase
   * @param {string} meetingId - ID de la réunion à transcrire
   * @param {string} audioUrl - URL du fichier audio
   * @param {object} options - Options supplémentaires (non utilisé avec Firebase Functions)
   * @returns {Promise<object>} - Résultat de la transcription
   */
  startTranscription: async (meetingId, audioUrl, options = {}) => {
    try {
      
      // Vérifier l'authentification via la fonction utilitaire
      if (!isAuthenticated()) {
        throw new Error("Vous devez être authentifié pour démarrer une transcription");
      }
      
      const userId = getCurrentUserId();
      
      // Obtenir le token d'authentification
      let idToken;
      
      // Utiliser l'API Firebase Auth standard
      const auth = getAuth();
      const firebaseUser = auth.currentUser;
      
      if (firebaseUser) {
        // Utilisateur Firebase déjà connecté
        
        try {
          idToken = await firebaseUser.getIdToken(true);
        } catch (e) {

          throw new Error(`Erreur d'authentification: ${e.message}`);
        }
      } else {
        
        // Essayer de récupérer et utiliser le custom token depuis localStorage
        try {
          const azureUserJson = localStorage.getItem('azureTestUser');
          if (!azureUserJson) {
            throw new Error("Aucune donnée d'authentification Azure trouvée");
          }
          
          const azureUser = JSON.parse(azureUserJson);
          if (!azureUser.firebaseToken) {
            throw new Error("Token Firebase non disponible pour l'utilisateur Azure");
          }

          // Utiliser l'API Firebase Auth modulaire
          const userCredential = await signInWithCustomToken(auth, azureUser.firebaseToken);
          const user = userCredential.user;
          
          // Obtenir un ID token après l'authentification réussie
          idToken = await user.getIdToken(true);
        } catch (e) {

          throw new Error(`Erreur d'authentification: ${e.message}. Essayez de vous déconnecter et vous reconnecter.`);
        }
      }
      
      if (!idToken) {
        throw new Error("Impossible d'obtenir un token d'authentification valide");
      }
      
      // Simplifier les paramètres de la requête
      const requestData = {
        data: {
          meetingId,
          audioUrl,
          // Passer le token Azure pour l'envoi d'email automatique après génération du résumé
          ...(options.azureToken && { azureToken: options.azureToken })
        }
      };

      // Faire l'appel HTTP direct à la fonction Firebase
      const response = await axios.post(START_TRANSCRIPTION_URL, 
        requestData,
        {
          headers: {
            'Authorization': `Bearer ${idToken}`,
            'Content-Type': 'application/json'
          }
        }
      );

      // Retourner le résultat
      return response.data.result || response.data;
    } catch (error) {

      return {
        success: false,
        message: `Erreur lors du démarrage de la transcription: ${error.message}`,
        meetingId
      };
    }
  },

  /**
   * Démarre plusieurs transcriptions en utilisant la fonction Firebase (multi-fichiers)
   * @param {string} meetingId - ID de la réunion à transcrire
   * @param {Array} audioFiles - Tableau des fichiers audio avec leurs informations
   * @param {object} options - Options supplémentaires (azureToken pour l'envoi d'email)
   * @returns {Promise<object>} - Résultat de la transcription multiple
   */
  startMultipleTranscriptions: async (meetingId, audioFiles, options = {}) => {
    try {
      
      // Vérifier l'authentification via la fonction utilitaire
      if (!isAuthenticated()) {
        throw new Error("Vous devez être authentifié pour démarrer une transcription");
      }
      
      const userId = getCurrentUserId();
      
      // Obtenir le token d'authentification
      let idToken;
      
      // Utiliser l'API Firebase Auth standard
      const auth = getAuth();
      const firebaseUser = auth.currentUser;
      
      if (firebaseUser) {
        // Utilisateur Firebase déjà connecté
        
        try {
          idToken = await firebaseUser.getIdToken(true);
        } catch (e) {

          throw new Error(`Erreur d'authentification: ${e.message}`);
        }
      } else {
        
        // Essayer de récupérer et utiliser le custom token depuis localStorage
        try {
          const azureUserJson = localStorage.getItem('azureTestUser');
          if (!azureUserJson) {
            throw new Error("Aucune donnée d'authentification Azure trouvée");
          }
          
          const azureUser = JSON.parse(azureUserJson);
          if (!azureUser.firebaseToken) {
            throw new Error("Token Firebase non disponible pour l'utilisateur Azure");
          }

          // Utiliser l'API Firebase Auth modulaire
          const userCredential = await signInWithCustomToken(auth, azureUser.firebaseToken);
          const user = userCredential.user;
          
          // Obtenir un ID token après l'authentification réussie
          idToken = await user.getIdToken(true);
        } catch (e) {

          throw new Error(`Erreur d'authentification: ${e.message}. Essayez de vous déconnecter et vous reconnecter.`);
        }
      }
      
      if (!idToken) {
        throw new Error("Impossible d'obtenir un token d'authentification valide");
      }
      
      // Préparer les données pour la fonction Firebase
      const requestData = {
        data: {
          meetingId,
          audioFiles: audioFiles.map((file, index) => ({
            audioUrl: file.audioUrl,
            fileName: file.fileName,
            fileSize: file.fileSize || 0,
            fileType: file.fileType || 'audio/mpeg',
            fileIndex: index
          })),
          // Passer le token Azure pour l'envoi d'email automatique après génération du résumé
          ...(options.azureToken && { azureToken: options.azureToken })
        }
      };

      // Faire l'appel HTTP direct à la fonction Firebase
      const response = await axios.post('https://us-central1-diotranscript.cloudfunctions.net/startMultipleTranscriptions', 
        requestData,
        {
          headers: {
            'Authorization': `Bearer ${idToken}`,
            'Content-Type': 'application/json'
          }
        }
      );

      // Retourner le résultat
      return response.data.result || response.data;
    } catch (error) {

      return {
        success: false,
        message: `Erreur lors du démarrage des transcriptions multiples: ${error.message}`,
        meetingId
      };
    }
  },

  /**
   * Récupère une transcription par ID
   * @param {string} id - ID de la transcription
   * @returns {Promise<object>} - Données de la transcription
   */
  getTranscriptionById: async (id) => {
    try {
      const transcriptionRef = doc(db, 'transcriptions', id);
      const transcriptionSnap = await getDoc(transcriptionRef);
      
      if (transcriptionSnap.exists()) {
        return { id: transcriptionSnap.id, ...transcriptionSnap.data() };
      }
      return null;
    } catch (error) {

      throw error;
    }
  },

  /**
   * Récupère une transcription par ID de réunion
   * @param {string} meetingId - ID de la réunion
   * @returns {Promise<object>} - Données de la transcription
   */
  getTranscriptionByMeetingId: async (meetingId) => {
    try {
      const currentUser = getAuth().currentUser;
      if (!currentUser) {
        throw new Error('Utilisateur non authentifié');
      }
      
      // Rechercher dans la collection des transcriptions
      const transcriptionsRef = collection(db, 'transcriptions');
      const q = query(transcriptionsRef, 
        where('meetingId', '==', meetingId),
        where('userId', '==', currentUser.uid)
      );
      
      const querySnapshot = await getDocs(q);
      if (!querySnapshot.empty) {
        const doc = querySnapshot.docs[0];
        return { id: doc.id, ...doc.data() };
      }
      
      return null;
    } catch (error) {

      throw error;
    }
  },

  /**
   * Annule une transcription en cours
   * @param {string} meetingId - ID de la réunion
   * @param {string} transcriptionId - ID de la transcription Firestore (optionnel)
   * @returns {Promise<object>} - Résultat de l'annulation
   */
  cancelTranscription: async (meetingId, transcriptionId = null) => {
    try {
      // Récupérer la réunion
      const meetingRef = doc(db, 'meetings', meetingId);
      const meetingSnap = await getDoc(meetingRef);
      
      if (!meetingSnap.exists()) {
        throw new Error('Réunion non trouvée');
      }
      
      const meeting = { id: meetingSnap.id, ...meetingSnap.data() };
      
      // Si l'ID de transcription n'est pas fourni mais que la réunion en a un
      if (!transcriptionId && meeting.transcriptionId) {
        transcriptionId = meeting.transcriptionId;
      }
      
      // Si nous avons un ID de transcription, mettre à jour son statut
      if (transcriptionId) {
        const transcriptionRef = doc(db, 'transcriptions', transcriptionId);
        const transcriptionSnap = await getDoc(transcriptionRef);
        
        if (transcriptionSnap.exists()) {
          // Mettre à jour le statut de la transcription dans Firestore
          await updateDoc(transcriptionRef, {
            status: 'cancelled',
            error: 'Transcription annulée par l\'utilisateur',
            updatedAt: new Date()
          });
          
        }
      }
      
      // Mettre à jour le statut de la réunion
      await updateDoc(meetingRef, {
        status: 'pending',
        updatedAt: new Date()
      });

      return {
        success: true,
        message: 'Transcription annulée avec succès',
        meetingId
      };
    } catch (error) {

      return {
        success: false,
        message: `Erreur lors de l'annulation de la transcription: ${error.message}`,
        meetingId
      };
    }
  },
  
  /**
   * Envoie un email de notification
   * @param {string} meetingId - ID de la réunion
   * @param {string} transcriptionId - ID de la transcription
   * @returns {Promise<object>} - Résultat de l'envoi d'email
   */
  sendTranscriptionCompletionEmail: async (meetingId, transcriptionId) => {
    try {
      
      // Vérifier l'authentification via la fonction utilitaire
      if (!isAuthenticated()) {
        throw new Error("Vous devez être authentifié pour envoyer un email");
      }
      
      const userId = getCurrentUserId();
      
      // Obtenir le token d'authentification
      let idToken;
      
      // Utiliser l'API Firebase Auth standard
      const auth = getAuth();
      const firebaseUser = auth.currentUser;
      
      if (firebaseUser) {
        // Utilisateur Firebase déjà connecté
        
        try {
          idToken = await firebaseUser.getIdToken(true);
        } catch (e) {

          throw new Error(`Erreur d'authentification: ${e.message}`);
        }
      } else {
        
        // Essayer de récupérer et utiliser le custom token depuis localStorage
        try {
          const azureUserJson = localStorage.getItem('azureTestUser');
          if (!azureUserJson) {
            throw new Error("Aucune donnée d'authentification Azure trouvée");
          }
          
          const azureUser = JSON.parse(azureUserJson);
          if (!azureUser.firebaseToken) {
            throw new Error("Token Firebase non disponible pour l'utilisateur Azure");
          }

          // Utiliser l'API Firebase Auth modulaire
          const userCredential = await signInWithCustomToken(auth, azureUser.firebaseToken);
          const user = userCredential.user;
          
          // Obtenir un ID token après l'authentification réussie
          idToken = await user.getIdToken(true);
        } catch (e) {

          throw new Error(`Erreur d'authentification: ${e.message}. Essayez de vous déconnecter et vous reconnecter.`);
        }
      }
      
      if (!idToken) {
        throw new Error("Impossible d'obtenir un token d'authentification valide");
      }
      
      // Faire l'appel HTTP direct à la fonction Firebase
      const response = await axios.post(SEND_EMAIL_URL, 
        {
          data: {
            meetingId,
            transcriptionId
          }
        },
        {
          headers: {
            'Authorization': `Bearer ${idToken}`,
            'Content-Type': 'application/json'
          }
        }
      );

      // Retourner le résultat
      return response.data.result || response.data;
    } catch (error) {

      return {
        success: false,
        message: `Erreur lors de l'envoi de l'email: ${error.message}`
      };
    }
  },

  /**
   * Met à jour le mapping speakers AssemblyAI -> participants dans Firestore
   * @param {string} transcriptionId - ID de la transcription
   * @param {object} speakerMapping - Objet { 'A': participantId, ... }
   * @returns {Promise<void>}
   */
  updateSpeakerMapping: async (transcriptionId, speakerMapping) => {
    try {
      const transcriptionRef = doc(db, 'transcriptions', transcriptionId);
      await updateDoc(transcriptionRef, {
        speakerMapping,
        updatedAt: new Date()
      });
    } catch (error) {

      throw error;
    }
  },

  /**
   * Met à jour le nom d'un participant dans les utterances d'une transcription
   * @param {string} transcriptionId - ID de la transcription
   * @param {string} oldName - Ancien nom du participant
   * @param {string} newName - Nouveau nom du participant
   * @returns {Promise<void>}
   */
  updateParticipantNameInUtterances: async (transcriptionId, oldName, newName) => {
    try {
      const transcriptionRef = doc(db, 'transcriptions', transcriptionId);
      const transcriptionSnap = await getDoc(transcriptionRef);
      if (!transcriptionSnap.exists()) return;

      const data = transcriptionSnap.data();
      if (!data.utterances) return;

      const updatedUtterances = data.utterances.map(u =>
        u.speaker === oldName ? { ...u, speaker: newName } : u
      );

      await updateDoc(transcriptionRef, { utterances: updatedUtterances, updatedAt: new Date() });
    } catch (error) {

      throw error;
    }
  },

  /**
   * Ecoute en temps réel une transcription par ID
   * @param {string} id - ID de la transcription
   * @param {function} callback - Fonction appelée à chaque mise à jour
   * @returns {function} - Fonction pour se désabonner
   */
  onTranscriptionById: (id, callback) => {
    const transcriptionRef = doc(db, 'transcriptions', id);
    return onSnapshot(transcriptionRef, (docSnap) => {
      if (docSnap.exists()) {
        callback({ id: docSnap.id, ...docSnap.data() });
      } else {
        callback(null);
      }
    }, (error) => {

      callback(null);
    });
  },

  /**
   * Ecoute en temps réel une transcription par meetingId
   * @param {string} meetingId
   * @param {function} callback
   * @returns {function}
   */
  onTranscriptionByMeetingId: (meetingId, callback) => {
    const currentUser = getAuth().currentUser;
    if (!currentUser) throw new Error('Utilisateur non authentifié');
    const transcriptionsRef = collection(db, 'transcriptions');
    const q = query(transcriptionsRef, 
      where('meetingId', '==', meetingId),
      where('userId', '==', currentUser.uid)
    );
    return onSnapshot(q, (querySnapshot) => {
      if (!querySnapshot.empty) {
        const doc = querySnapshot.docs[0];
        callback({ id: doc.id, ...doc.data() });
      } else {
        callback(null);
      }
    }, (error) => {

      callback(null);
    });
  },

  /**
   * Ecoute en temps réel toutes les transcriptions d'un utilisateur
   * @param {string} userId
   * @param {function} callback
   * @returns {function}
   */
  onUserTranscriptions: (userId, callback) => {
    const transcriptionsRef = collection(db, 'transcriptions');
    const q = query(transcriptionsRef, where('userId', '==', userId));
    return onSnapshot(q, (querySnapshot) => {
      const transcriptions = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      callback(transcriptions);
    }, (error) => {

      callback([]);
    });
  }
};

// Génère une transcription à partir d'un fichier audio
export const generateTranscription = async (file, options = {}) => {
  try {
    // Récupérer les paramètres de l'application
    const appSettings = await getAppSettings();
    const openAISettings = appSettings.openAISettings || {};
    const transcriptionSettings = appSettings.transcriptionSettings || {};
    
    // Préparer les options avec les paramètres des appSettings
    const completeOptions = {
      model: options.model || transcriptionSettings.defaultModel || 'whisper-1',
      language: options.language || transcriptionSettings.defaultLanguage || 'fr',
      systemPrompt: openAISettings.transcriptionPrompt || "Transcription d'une réunion professionnelle en français.",
      maxTokens: openAISettings.transcriptionMaxTokens || 4096,
      temperature: options.temperature || openAISettings.temperature || 0.7
    };

    // Créer l'objet File à partir de l'URL du fichier ou utiliser le fichier directement
    const fileObj = file instanceof File ? file : null;
    
    if (!fileObj) {
      throw new Error('Format de fichier non pris en charge');
    }
    
    // Vérifier la taille du fichier (max 25MB pour OpenAI)
    const maxSize = 25 * 1024 * 1024;
    if (fileObj.size > maxSize) {
      throw new Error(`Le fichier est trop volumineux (max: 25MB, actuel: ${Math.round(fileObj.size / (1024 * 1024))}MB)`);
    }
    
    // Vérifier le type de fichier
    const validTypes = ['audio/mpeg', 'audio/mp3', 'audio/wav', 'audio/x-wav', 'audio/ogg', 'audio/webm', 'audio/mp4', 'video/mp4', 'video/webm', 'video/ogg'];
    if (!validTypes.includes(fileObj.type)) {
      throw new Error(`Type de fichier non pris en charge: ${fileObj.type}. Les formats supportés sont: MP3, MP4, MPEG, WEBM, WAV, OGG`);
    }

    // Créer un document Firestore pour suivre la progression
    const auth = getAuth();
    const user = auth.currentUser;
    
    if (!user) {
      throw new Error('Utilisateur non authentifié');
    }
    
    // Créer un document initial dans Firestore
    const timestamp = new Date().toISOString();
    const transcriptionRef = await addDoc(collection(db, 'transcriptions'), {
      fileName: fileObj.name,
      fileSize: fileObj.size,
      fileType: fileObj.type,
      status: 'uploading',
      progress: 0,
      userId: user.uid,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      clientTimestamp: timestamp,
      options: completeOptions
    });

    // Créer une référence au stockage
    const storageRef = ref(storage, `transcriptions/${user.uid}/${transcriptionRef.id}/${fileObj.name}`);
    
    // Créer la tâche d'upload
    const uploadTask = uploadBytesResumable(storageRef, fileObj);
    
    // Suivre la progression de l'upload
    uploadTask.on('state_changed', 
      (snapshot) => {
        const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
        updateDoc(transcriptionRef, {
          progress,
          status: 'uploading',
          updatedAt: serverTimestamp()
        });
      },
      (error) => {
        // Gérer les erreurs d'upload

        updateDoc(transcriptionRef, {
          status: 'error',
          error: error.message,
          updatedAt: serverTimestamp()
        });
        throw error;
      },
      async () => {
        // Upload terminé avec succès
        
        try {
          // Obtenir l'URL du fichier
          const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
          
          // Mettre à jour le document avec l'URL et changer le statut
          await updateDoc(transcriptionRef, {
            status: 'processing',
            fileUrl: downloadURL,
            updatedAt: serverTimestamp()
          });

          // Appeler la fonction Firebase pour démarrer la transcription
          const functions = getFunctions();
          const generateTranscriptionFn = httpsCallable(functions, 'generateTranscription');

          const result = await generateTranscriptionFn({
            transcriptionId: transcriptionRef.id,
            fileUrl: downloadURL,
            options: completeOptions
          });

          return {
            transcriptionId: transcriptionRef.id,
            ...result.data
          };
        } catch (error) {

          // Mettre à jour le document avec l'erreur
          await updateDoc(transcriptionRef, {
            status: 'error',
            error: error.message,
            updatedAt: serverTimestamp()
          });
          
          throw error;
        }
      }
    );
    
    // Retourner l'ID de la transcription pour pouvoir suivre sa progression
    return {
      transcriptionId: transcriptionRef.id,
      status: 'uploading'
    };
  } catch (error) {

    throw error;
  }
};

/**
 * Récupère les statistiques de coût des résumés OpenAI
 * @param {Object} options - Options de filtrage
 * @param {string} options.startDate - Date de début (format YYYY-MM-DD)
 * @param {string} options.endDate - Date de fin (format YYYY-MM-DD)
 * @param {boolean} options.allUsers - Pour les admins : récupérer tous les utilisateurs
 * @returns {Promise<Object>} - Statistiques de coût
 */
export const getCostStatistics = async (options = {}) => {
  try {
    
    // Vérifier l'authentification
    const user = getAuth().currentUser;
    if (!user) {
      throw new Error('Utilisateur non authentifié');
    }
    
    // Construire l'URL avec les paramètres de requête
    const params = new URLSearchParams();
    if (options.startDate) params.append('startDate', options.startDate);
    if (options.endDate) params.append('endDate', options.endDate);
    if (options.allUsers) params.append('allUsers', 'true');
    
    const url = `${COST_STATISTICS_URL}${params.toString() ? '?' + params.toString() : ''}`;
    
    // Obtenir le token d'authentification Firebase
    const idToken = await user.getIdToken();
    
    // Faire l'appel à l'API
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${idToken}`
      }
    });
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `Erreur HTTP: ${response.status}`);
    }
    
    const data = await response.json();
    
    if (!data.success) {
      throw new Error(data.message || 'Erreur lors de la récupération des statistiques');
    }

    return {
      success: true,
      statistics: data.statistics,
      summaries: data.summaries
    };
    
  } catch (error) {

    return {
      success: false,
      message: error.message
    };
  }
};

/**
 * Formate le coût pour l'affichage
 * @param {number} cost - Coût en euros
 * @returns {string} - Coût formaté (ex: "0,025 €")
 */
export const formatCost = (cost) => {
  if (!cost || cost === 0) return '0 €';
  
  // Pour les très petits montants, afficher plus de décimales
  if (cost < 0.01) {
    return `${cost.toFixed(6)} €`;
  } else if (cost < 1) {
    return `${cost.toFixed(3)} €`;
  } else {
    return `${cost.toFixed(2)} €`;
  }
};

/**
 * Formate le nombre de tokens pour l'affichage
 * @param {number} tokens - Nombre de tokens
 * @returns {string} - Tokens formatés (ex: "1,2K" ou "1 543")
 */
export const formatTokens = (tokens) => {
  if (!tokens || tokens === 0) return '0';
  
  if (tokens >= 1000000) {
    return `${(tokens / 1000000).toFixed(1)}M`;
  } else if (tokens >= 1000) {
    return `${(tokens / 1000).toFixed(1)}K`;
  } else {
    return tokens.toLocaleString('fr-FR');
  }
};

export default transcriptionService; 